import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase';

const ALLOWED_EXTENSIONS = new Set(['pdf', 'doc', 'docx']);
const ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export interface StoredCareerFile {
  fileName: string;
  storagePath: string;
  downloadUrl: string;
  contentType: string;
  size: number;
}

export interface CareerFileUploadResult {
  applicationId: string;
  resume: StoredCareerFile;
  coverLetter: StoredCareerFile;
}

function normalizeSegment(value: string): string {
  const trimmed = value.trim().toLowerCase();
  return trimmed
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function extensionFromFile(file: File): string {
  const fromName = file.name.split('.').pop()?.trim().toLowerCase() ?? '';
  if (ALLOWED_EXTENSIONS.has(fromName)) return fromName;

  if (file.type === 'application/pdf') return 'pdf';
  if (file.type === 'application/msword') return 'doc';
  if (
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return 'docx';
  }
  return '';
}

function validateUploadFile(file: File, label: string): { ext: string; mime: string } {
  const ext = extensionFromFile(file);
  if (!ext) {
    throw new Error(`${label} must be PDF, DOC, or DOCX.`);
  }
  if (file.size <= 0) {
    throw new Error(`${label} is empty.`);
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(`${label} must be 10MB or smaller.`);
  }
  const mime =
    file.type && ALLOWED_MIME_TYPES.has(file.type)
      ? file.type
      : ext === 'pdf'
      ? 'application/pdf'
      : ext === 'doc'
      ? 'application/msword'
      : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

  return { ext, mime };
}

async function uploadOne(
  file: File,
  path: string,
  contentType: string,
  metadata: Record<string, string>,
): Promise<StoredCareerFile> {
  const target = storageRef(storage, path);
  await uploadBytes(target, file, {
    contentType,
    cacheControl: 'private,max-age=0,no-cache',
    customMetadata: metadata,
  });
  const downloadUrl = await getDownloadURL(target);
  return {
    fileName: file.name,
    storagePath: path,
    downloadUrl,
    contentType,
    size: file.size,
  };
}

export async function uploadCareerApplicationFiles({
  jobId,
  requisitionId,
  applicantEmail,
  resumeFile,
  coverLetterFile,
}: {
  jobId: string;
  requisitionId: string;
  applicantEmail: string;
  resumeFile: File;
  coverLetterFile: File;
}): Promise<CareerFileUploadResult> {
  const resumeInfo = validateUploadFile(resumeFile, 'Resume');
  const coverInfo = validateUploadFile(coverLetterFile, 'Cover letter');
  const now = Date.now();
  const rand = Math.random().toString(36).slice(2, 8);
  const safeJob = normalizeSegment(jobId || requisitionId || 'job');
  const safeReq = normalizeSegment(requisitionId || 'req');
  const safeEmail = normalizeSegment(applicantEmail || 'applicant');
  const applicationId = `app_${now}_${rand}`;
  const basePath = `career-applications/${safeJob}/${safeReq}/${safeEmail}/${applicationId}`;

  const [resume, coverLetter] = await Promise.all([
    uploadOne(
      resumeFile,
      `${basePath}/resume.${resumeInfo.ext}`,
      resumeInfo.mime,
      {
        role: 'resume',
        jobId: safeJob,
        requisitionId: safeReq,
        applicant: safeEmail,
      },
    ),
    uploadOne(
      coverLetterFile,
      `${basePath}/cover-letter.${coverInfo.ext}`,
      coverInfo.mime,
      {
        role: 'cover-letter',
        jobId: safeJob,
        requisitionId: safeReq,
        applicant: safeEmail,
      },
    ),
  ]);

  return {
    applicationId,
    resume,
    coverLetter,
  };
}
